import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {css} from "@panda/css";

import {Loader} from "@pg-design/loader-module";
import {useActiveElementId, useChangeVisibility} from "@pg-mono/hooks";

import {LeadModal} from "../../application/components/LeadModal";
import {ScrollRefOffset} from "../../atoms/ScrollRefOffset";
import {OfferDisplayType} from "../../offer/helpers/OfferDisplayType";
import {OfferListMapModal} from "../../offer/list/components/OfferListMapModal/OfferListMapModal";
import {OfferType} from "../../offer/types/OfferType";
import {useAppSelector} from "../../store/hooks";
import {vendorDetailPageview} from "../../tracking/algolytics_hits/vendor_detail_pageview";
import {ViewType} from "../../view_type/ViewType";
import {useGetVendorDetailAllActiveOffersQuery} from "../api/get_vendor_detail_all_active_offers";
import {useGetVendorDetailOfferListMapQuery} from "../api/get_vendor_detail_offer_list_map";
import {VendorDetailActiveOffers} from "../components/detail/VendorDetailActiveOffers";
import {VendorDetailArticles} from "../components/detail/VendorDetailArticles";
import {VendorDetailCommercials} from "../components/detail/VendorDetailCommercials";
import {VendorDetailContact} from "../components/detail/VendorDetailContact";
import {VendorDetailDescription} from "../components/detail/VendorDetailDescription";
import {VendorDetailHeader} from "../components/detail/VendorDetailHeader";
import {VendorDetailPromotions} from "../components/detail/VendorDetailPromotions";
import {VendorDetailSearchFilters} from "../components/detail/VendorDetailSearchFilters";
import {VendorDetailSoldOffers} from "../components/detail/VendorDetailSoldOffers";
import {VendorBreadcrumbs} from "../components/VendorBreadcrumbs";
import {VendorContainer} from "../components/VendorContainer";
import {VENDOR_DETAIL_OFFERS_MAX_PAGE_SIZE} from "../constants/vendor_detail_offers_max_page_size";
import {doesVendorHaveAnniversaryAward} from "../helpers/does_vendor_have_anniversary_award";
import {convertToVendorDetailViewOffersPayload} from "../utils/convert_to_vendor_detail_view_offers_payload";
import {isActiveVendorOffer} from "../utils/is_active_vendor_offer";

export const VendorDetailView = () => {
    const vendorDetail = useAppSelector((state) => state.vendor.detail);
    const variables = useAppSelector((state) => state.variables);
    const articles = useAppSelector((state) => state.vendor.detail.articles ?? []);
    const rankings = useAppSelector((state) => state.vendor.detail.rankings ?? []);
    const favouriteOffers = useAppSelector((state) => state.favourite.favourites.offers);
    const appliedOffers = useAppSelector((state) => state.offerListMap.appliedOffers.data);
    const region = useAppSelector((state) => state.vendorDetailViewSlice.activeOffersListParams.region);
    const selectedOfferType = useAppSelector((state) => state.vendorDetailViewSlice.activeOffersListParams.offerType);

    const selectedRegionName = useMemo(() => {
        if (!region || !vendorDetail.cities) {
            return null;
        }
        const foundCity = vendorDetail.cities.find((city) => city.id === region);
        return foundCity?.name ?? null;
    }, [region, vendorDetail.cities]);

    const {vendor} = vendorDetail;

    useEffect(() => {
        vendor && vendorDetailPageview(vendor);
    }, [vendor]);

    const fullQueryActiveOffers = {
        ...convertToVendorDetailViewOffersPayload({
            vendorId: vendor?.id ?? 0,
            region,
            displayType: OfferDisplayType.FOR_SALE,
            pageSize: VENDOR_DETAIL_OFFERS_MAX_PAGE_SIZE
        }),
        page: 1
    };

    const {data: activeOffers} = useGetVendorDetailAllActiveOffersQuery(fullQueryActiveOffers);

    const {data: activeFlatsAndHouses} = useGetVendorDetailOfferListMapQuery({vendor: vendor?.id ?? 0, region});

    const activeFlats = activeFlatsAndHouses?.filter((offer) => offer.type === OfferType.FLAT && isActiveVendorOffer(offer)) ?? [];
    const activeHouses = activeFlatsAndHouses?.filter((offer) => offer.type === OfferType.HOUSE && isActiveVendorOffer(offer)) ?? [];
    const activeCommercials = activeFlatsAndHouses?.filter((offer) => offer.type === OfferType.COMMERCIAL && isActiveVendorOffer(offer)) ?? [];

    const refAboutUs = useRef<HTMLTableSectionElement>(null);
    const refPromotions = useRef<HTMLTableSectionElement>(null);
    const refOffers = useRef<HTMLTableSectionElement>(null);
    const refContact = useRef<HTMLTableSectionElement>(null);
    const refElems = useMemo(() => ({refPromotions, refOffers, refAboutUs, refContact}), []);

    const vendorAward = !!vendor && doesVendorHaveAnniversaryAward(vendor);

    const filteredPromotions = useMemo(() => {
        if (!vendor?.promotions) {
            return [];
        }
        if (!region) {
            return vendor.promotions;
        }

        return vendor.promotions.filter((promotion) => promotion.offer.region?.stats?.region_type_city?.id === region);
    }, [vendor?.promotions, region]);

    const hasPromotions = filteredPromotions.length > 0;
    const hasAwards = rankings.length > 0 || vendorAward;
    const hasActiveFlatsAndHouses = activeFlatsAndHouses ? activeFlatsAndHouses.length > 0 : false;

    const getElems = useCallback(() => {
        const res = [];

        res.push("about_us");

        if (hasPromotions) {
            res.push("promotions");
        }

        if (hasActiveFlatsAndHouses) {
            res.push("offers");
        }

        res.push("contact");

        return res;
    }, [hasAwards, hasPromotions, hasActiveFlatsAndHouses]);

    const [visibleElem, changeVisibility, viewportHeight] = useChangeVisibility(getElems, 100);

    const ID_MAPPING: Record<string, string> = {
        "o-nas": "about_us",
        nagrody: "awards",
        "oferty-specjalne": "promotions",
        oferta: "offers",
        kontakt: "contact"
    };

    const activeId = useActiveElementId(
        Object.keys(ID_MAPPING).filter((id) => {
            switch (ID_MAPPING[id]) {
                case "promotions":
                    return hasPromotions;
                case "offers":
                    return hasActiveFlatsAndHouses;
                default:
                    return true;
            }
        }),
        {
            threshold: 0,
            rootMargin: `0px 0px -${viewportHeight}px 0px`
        }
    );

    useEffect(() => {
        if (activeId) {
            const mappedId = ID_MAPPING[activeId] || activeId;

            changeVisibility(mappedId)(true);
        } else {
            changeVisibility(visibleElem || "about_us")(false);
        }
    }, [activeId, changeVisibility, visibleElem]);

    // Offer map modal
    const [isMapModalOpen, setIsMapModalOpen] = useState(false);

    if (!vendor) {
        return (
            <div className={loaderWrapper}>
                <Loader />
            </div>
        );
    }

    return (
        <article>
            <div>
                <VendorContainer>
                    <VendorBreadcrumbs />
                </VendorContainer>

                <VendorContainer>
                    <div className={css({pt: 4, pb: 2})}>
                        <VendorDetailHeader
                            vendor={vendor}
                            onShowMapClick={() => setIsMapModalOpen(true)}
                            hasActiveOffers={hasActiveFlatsAndHouses}
                            selectedRegionName={selectedRegionName}
                            selectedOfferType={selectedOfferType}
                        />
                    </div>
                </VendorContainer>

                {hasActiveFlatsAndHouses && (
                    <VendorContainer>
                        <VendorDetailSearchFilters
                            vendor={vendor}
                            cities={vendorDetail.cities ?? []}
                            hasActiveFlats={activeFlats.length > 0}
                            hasActiveHouses={activeHouses.length > 0}
                            hasActiveCommercials={activeCommercials.length > 0}
                        />
                    </VendorContainer>
                )}

                <VendorContainer>
                    {hasActiveFlatsAndHouses && activeOffers && (
                        <section id="oferta" className={css({pb: 3, md: {pb: 7}})}>
                            <ScrollRefOffset offsetTop={-100} ref={refElems.refOffers} />

                            <VendorDetailActiveOffers offersInRegionRequest={vendorDetail.offersInRegionRequest} vendor={vendor} />
                        </section>
                    )}

                    {hasPromotions && (
                        <section id="oferty-specjalne" className={generalSectionStyle}>
                            <ScrollRefOffset offsetTop={-100} ref={refElems.refPromotions} />

                            <VendorDetailPromotions promotions={filteredPromotions} vendorSlug={vendor.slug} />
                        </section>
                    )}

                    <VendorDetailCommercials vendorId={vendor.id} vendorSlug={vendor.slug}>
                        {(sectionContent) => <section className={generalSectionStyle}>{sectionContent}</section>}
                    </VendorDetailCommercials>

                    <section id="o-nas" className={generalSectionStyle}>
                        <ScrollRefOffset offsetTop={-100} ref={refElems.refAboutUs} />

                        <VendorDetailDescription
                            variables={variables}
                            vendor={vendor}
                            flatsOffers={activeFlats}
                            housesOffers={activeHouses}
                            commercialsOffers={activeCommercials}
                        />
                    </section>

                    <section className={generalSectionStyle}>
                        <VendorDetailSoldOffers vendorSlug={vendor.slug} vendorDetail={vendor} />
                    </section>

                    <section id="kontakt" className={generalSectionStyle}>
                        <ScrollRefOffset offsetTop={-100} ref={refElems.refContact} />

                        <VendorDetailContact vendor={vendor} hasActiveOffers={hasActiveFlatsAndHouses} />
                    </section>
                </VendorContainer>
            </div>

            {articles.length > 0 && (
                <section>
                    <VendorDetailArticles vendorName={vendor.name} articles={articles} />
                </section>
            )}

            <LeadModal />

            <OfferListMapModal
                viewType={ViewType.VENDOR}
                offerList={activeFlatsAndHouses ?? []}
                isModalOpen={isMapModalOpen}
                onModalClose={() => setIsMapModalOpen(false)}
                favouriteOffers={favouriteOffers}
                appliedOffers={appliedOffers}
            />
        </article>
    );
};

const loaderWrapper = css({
    borderRadius: 1,
    height: 400,
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
});

const generalSectionStyle = css({
    pt: 3,
    pb: 3,
    md: {
        pt: 7,
        pb: 7
    }
});
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {css} from "@panda/css";

import {Loader} from "@pg-design/loader-module";
import {useActiveElementId, useChangeVisibility} from "@pg-mono/hooks";

import {LeadModal} from "../../application/components/LeadModal";
import {ScrollRefOffset} from "../../atoms/ScrollRefOffset";
import {OfferDisplayType} from "../../offer/helpers/OfferDisplayType";
import {OfferListMapModal} from "../../offer/list/components/OfferListMapModal/OfferListMapModal";
import {OfferType} from "../../offer/types/OfferType";
import {useAppSelector} from "../../store/hooks";
import {vendorDetailPageview} from "../../tracking/algolytics_hits/vendor_detail_pageview";
import {ViewType} from "../../view_type/ViewType";
import {useGetVendorDetailAllActiveOffersQuery} from "../api/get_vendor_detail_all_active_offers";
import {useGetVendorDetailOfferListMapQuery} from "../api/get_vendor_detail_offer_list_map";
import {VendorDetailActiveOffers} from "../components/detail/VendorDetailActiveOffers";
import {VendorDetailArticles} from "../components/detail/VendorDetailArticles";
import {VendorDetailCommercials} from "../components/detail/VendorDetailCommercials";
import {VendorDetailContact} from "../components/detail/VendorDetailContact";
import {VendorDetailDescription} from "../components/detail/VendorDetailDescription";
import {VendorDetailHeader} from "../components/detail/VendorDetailHeader";
import {VendorDetailPromotions} from "../components/detail/VendorDetailPromotions";
import {VendorDetailSearchFilters} from "../components/detail/VendorDetailSearchFilters";
import {VendorDetailSoldOffers} from "../components/detail/VendorDetailSoldOffers";
import {VendorBreadcrumbs} from "../components/VendorBreadcrumbs";
import {VendorContainer} from "../components/VendorContainer";
import {VENDOR_DETAIL_OFFERS_MAX_PAGE_SIZE} from "../constants/vendor_detail_offers_max_page_size";
import {doesVendorHaveAnniversaryAward} from "../helpers/does_vendor_have_anniversary_award";
import {convertToVendorDetailViewOffersPayload} from "../utils/convert_to_vendor_detail_view_offers_payload";
import {isActiveVendorOffer} from "../utils/is_active_vendor_offer";

export const VendorDetailView = () => {
    const vendorDetail = useAppSelector((state) => state.vendor.detail);
    const variables = useAppSelector((state) => state.variables);
    const articles = useAppSelector((state) => state.vendor.detail.articles ?? []);
    const rankings = useAppSelector((state) => state.vendor.detail.rankings ?? []);
    const favouriteOffers = useAppSelector((state) => state.favourite.favourites.offers);
    const appliedOffers = useAppSelector((state) => state.offerListMap.appliedOffers.data);
    const region = useAppSelector((state) => state.vendorDetailViewSlice.activeOffersListParams.region);
    const selectedOfferType = useAppSelector((state) => state.vendorDetailViewSlice.activeOffersListParams.offerType);

    const selectedRegionName = useMemo(() => {
        if (!region || !vendorDetail.cities) {
            return null;
        }
        const foundCity = vendorDetail.cities.find((city) => city.id === region);
        return foundCity?.name ?? null;
    }, [region, vendorDetail.cities]);

    const {vendor} = vendorDetail;

    useEffect(() => {
        vendor && vendorDetailPageview(vendor);
    }, [vendor]);

    const fullQueryActiveOffers = {
        ...convertToVendorDetailViewOffersPayload({
            vendorId: vendor?.id ?? 0,
            region,
            displayType: OfferDisplayType.FOR_SALE,
            pageSize: VENDOR_DETAIL_OFFERS_MAX_PAGE_SIZE
        }),
        page: 1
    };

    const {data: activeOffers} = useGetVendorDetailAllActiveOffersQuery(fullQueryActiveOffers);

    const {data: activeFlatsAndHouses} = useGetVendorDetailOfferListMapQuery({vendor: vendor?.id ?? 0, region});

    const activeFlats = activeFlatsAndHouses?.filter((offer) => offer.type === OfferType.FLAT && isActiveVendorOffer(offer)) ?? [];
    const activeHouses = activeFlatsAndHouses?.filter((offer) => offer.type === OfferType.HOUSE && isActiveVendorOffer(offer)) ?? [];
    const activeCommercials = activeFlatsAndHouses?.filter((offer) => offer.type === OfferType.COMMERCIAL && isActiveVendorOffer(offer)) ?? [];

    const refAboutUs = useRef<HTMLTableSectionElement>(null);
    const refPromotions = useRef<HTMLTableSectionElement>(null);
    const refOffers = useRef<HTMLTableSectionElement>(null);
    const refContact = useRef<HTMLTableSectionElement>(null);
    const refElems = useMemo(() => ({refPromotions, refOffers, refAboutUs, refContact}), []);

    const vendorAward = !!vendor && doesVendorHaveAnniversaryAward(vendor);

    const filteredPromotions = useMemo(() => {
        if (!vendor?.promotions) {
            return [];
        }
        if (!region) {
            return vendor.promotions;
        }

        return vendor.promotions.filter((promotion) => promotion.offer.region?.stats?.region_type_city?.id === region);
    }, [vendor?.promotions, region]);

    const hasPromotions = filteredPromotions.length > 0;
    const hasAwards = rankings.length > 0 || vendorAward;
    const hasActiveFlatsAndHouses = activeFlatsAndHouses ? activeFlatsAndHouses.length > 0 : false;

    const getElems = useCallback(() => {
        const res = [];

        res.push("about_us");

        if (hasPromotions) {
            res.push("promotions");
        }

        if (hasActiveFlatsAndHouses) {
            res.push("offers");
        }

        res.push("contact");

        return res;
    }, [hasAwards, hasPromotions, hasActiveFlatsAndHouses]);

    const [visibleElem, changeVisibility, viewportHeight] = useChangeVisibility(getElems, 100);

    const ID_MAPPING: Record<string, string> = {
        "o-nas": "about_us",
        nagrody: "awards",
        "oferty-specjalne": "promotions",
        oferta: "offers",
        kontakt: "contact"
    };

    const activeId = useActiveElementId(
        Object.keys(ID_MAPPING).filter((id) => {
            switch (ID_MAPPING[id]) {
                case "promotions":
                    return hasPromotions;
                case "offers":
                    return hasActiveFlatsAndHouses;
                default:
                    return true;
            }
        }),
        {
            threshold: 0,
            rootMargin: `0px 0px -${viewportHeight}px 0px`
        }
    );

    useEffect(() => {
        if (activeId) {
            const mappedId = ID_MAPPING[activeId] || activeId;

            changeVisibility(mappedId)(true);
        } else {
            changeVisibility(visibleElem || "about_us")(false);
        }
    }, [activeId, changeVisibility, visibleElem]);

    // Offer map modal
    const [isMapModalOpen, setIsMapModalOpen] = useState(false);

    if (!vendor) {
        return (
            <div className={loaderWrapper}>
                <Loader />
            </div>
        );
    }

    return (
        <article>
            <div>
                <VendorContainer>
                    <VendorBreadcrumbs />
                </VendorContainer>

                <VendorContainer>
                    <div className={css({pt: 4, pb: 2})}>
                        <VendorDetailHeader
                            vendor={vendor}
                            onShowMapClick={() => setIsMapModalOpen(true)}
                            hasActiveOffers={hasActiveFlatsAndHouses}
                            selectedRegionName={selectedRegionName}
                            selectedOfferType={selectedOfferType}
                        />
                    </div>
                </VendorContainer>

                {hasActiveFlatsAndHouses && (
                    <VendorContainer>
                        <VendorDetailSearchFilters
                            vendor={vendor}
                            cities={vendorDetail.cities ?? []}
                            hasActiveFlats={activeFlats.length > 0}
                            hasActiveHouses={activeHouses.length > 0}
                            hasActiveCommercials={activeCommercials.length > 0}
                        />
                    </VendorContainer>
                )}

                <VendorContainer>
                    {hasActiveFlatsAndHouses && activeOffers && (
                        <section id="oferta" className={css({pb: 3, md: {pb: 7}})}>
                            <ScrollRefOffset offsetTop={-100} ref={refElems.refOffers} />

                            <VendorDetailActiveOffers offersInRegionRequest={vendorDetail.offersInRegionRequest} vendor={vendor} />
                        </section>
                    )}

                    {hasPromotions && (
                        <section id="oferty-specjalne" className={generalSectionStyle}>
                            <ScrollRefOffset offsetTop={-100} ref={refElems.refPromotions} />

                            <VendorDetailPromotions promotions={filteredPromotions} vendorSlug={vendor.slug} />
                        </section>
                    )}

                    <VendorDetailCommercials vendorId={vendor.id} vendorSlug={vendor.slug}>
                        {(sectionContent) => <section className={generalSectionStyle}>{sectionContent}</section>}
                    </VendorDetailCommercials>

                    <section id="o-nas" className={generalSectionStyle}>
                        <ScrollRefOffset offsetTop={-100} ref={refElems.refAboutUs} />

                        <VendorDetailDescription
                            variables={variables}
                            vendor={vendor}
                            flatsOffers={activeFlats}
                            housesOffers={activeHouses}
                            commercialsOffers={activeCommercials}
                        />
                    </section>

                    <section className={generalSectionStyle}>
                        <VendorDetailSoldOffers vendorSlug={vendor.slug} vendorDetail={vendor} />
                    </section>

                    <section id="kontakt" className={generalSectionStyle}>
                        <ScrollRefOffset offsetTop={-100} ref={refElems.refContact} />

                        <VendorDetailContact vendor={vendor} hasActiveOffers={hasActiveFlatsAndHouses} />
                    </section>
                </VendorContainer>
            </div>

            {articles.length > 0 && (
                <section>
                    <VendorDetailArticles vendorName={vendor.name} articles={articles} />
                </section>
            )}

            <LeadModal />

            <OfferListMapModal
                viewType={ViewType.VENDOR}
                offerList={activeFlatsAndHouses ?? []}
                isModalOpen={isMapModalOpen}
                onModalClose={() => setIsMapModalOpen(false)}
                favouriteOffers={favouriteOffers}
                appliedOffers={appliedOffers}
            />
        </article>
    );
};

const loaderWrapper = css({
    borderRadius: 1,
    height: 400,
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
});

const generalSectionStyle = css({
    pt: 3,
    pb: 3,
    md: {
        pt: 7,
        pb: 7
    }
});
